/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import GenericModal from "components/Modal";
import Button from "components/Button";

interface ConfirmChangesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    changes: Record<string, any>;
    initialValues: Record<string, any>;
    isSubmitting: boolean;
}

const formatDuration = (seconds: number): string => {
    if (typeof seconds !== "number") return String(seconds);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
};

const getDayName = (dayNum: number): string => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayNum] || "";
};

const LABEL_MAP: Record<string, string> = {
    screenshotEnabled: "Screenshot Capture",
    screenshotInterval: "Screenshot Interval",
    screenshotQuality: "Screenshot Quality",
    liveScreenQuality: "Live Screen Quality",
    dashboardEnabled: "User Dashboard",
    appTrackingEnabled: "App Tracking",
    urlTrackingEnabled: "URL Tracking",
    presenceTrackingEnabled: "Face Detection",
    deviceTrackingEnabled: "Device Tracking",
    wifiTrackingEnabled: "WiFi Tracking",
    liveScreenEnabled: "Live Screen",
    deviceCheckInterval: "Device Check Interval",
    sessionUploadInterval: "Session Upload Interval",
    urlSessionUploadInterval: "URL Session Upload",
    deviceStatusUploadInterval: "Device Status Upload",
    presenceCheckInterval: "Presence Check Interval",
    presenceUploadInterval: "Presence Upload",
    scheduleTrackingEnabled: "Schedule Tracking",
    workStartTime: "Work Start Time",
    workEndTime: "Work End Time",
    workDays: "Work Days",
    idleThreshold: "Idle Threshold",
    dailyBreakLimit: "Daily Break Limit",
};

const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
    if (key.includes("Interval") || key.includes("Limit") || key.includes("Threshold")) {
        return formatDuration(Number(value));
    }
    if (key === "workDays" && Array.isArray(value)) {
        if (value.length === 0) return "None";
        return value.map((v) => getDayName(v)).join(", ");
    }
    return String(value).toUpperCase();
};

const ConfirmChangesModal: React.FC<ConfirmChangesModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    changes,
    initialValues,
    isSubmitting,
}) => {
    const changeKeys = Object.keys(changes);

    return (
        <GenericModal isOpen={isOpen} onClose={!isSubmitting ? onClose : () => { }} size="lg">
            <div className="p-0">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    Confirm Setting Overrides
                </h2>

                <div className="max-h-[60vh] overflow-y-auto pr-2">
                    <ul className="space-y-3">
                        {changeKeys.map((key) => (
                            <li
                                key={key}
                                className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 gap-3"
                            >
                                <span className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                                    {LABEL_MAP[key] || key}
                                </span>
                                <div className="text-sm flex flex-wrap items-center gap-2 bg-white dark:bg-gray-900 px-3 py-1.5 rounded border border-gray-100 dark:border-gray-600 shadow-sm">
                                    <span className="line-through text-gray-400 dark:text-gray-500">
                                        {formatValue(key, initialValues[key])}
                                    </span>
                                    <span className="text-gray-400 dark:text-gray-500">➔</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400">
                                        {formatValue(key, changes[key])}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex justify-end gap-3 mt-2 pt-1 border-gray-200 dark:border-gray-700">
                    <Button type="button" color="gray" onClick={onClose} disabled={isSubmitting} className="bg-gray-500 hover:bg-gray-600">
                        Cancel
                    </Button>
                    <Button type="button" color="blue" onClick={onConfirm} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </span>
                        ) : "Confirm"}
                    </Button>
                </div>
            </div>
        </GenericModal>
    );
};

export default ConfirmChangesModal;